import React, { useState, useEffect } from "react";
import {
  Calendar,
  CreditCard,
  Clock,
  Edit,
  MapPin,
  User,
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Building2,
} from "lucide-react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import HTabsNavigation from "../../../hcomponents/HTabsNavigation";
import { Badge } from "../../../shadcn/ui/badge";
import { Card, CardContent } from "../../../shadcn/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shadcn/ui/table";
import { Constants } from "../../../utils/constants";
import { getEventName } from "../../../utils/spaceNamingUtils";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { getUserTimezone, convertToUTC, getCurrentTimezoneOffset } from "../../../utils/timezoneUtils";
import { useSubscription } from "../../../hooks/useSubscription";
import useBookingFlow from "../../../hooks/useBookingFlow";
import { useHistory } from "react-router-dom";
import BookingViewToggle from "../host-customer-book-space/components/BookingViewToggle";

import toastUtils from "../../../utils/toast";
import { showApiMessage } from "../../../utils/messageHandler";
import { canModify, canCheckIn, canCheckOut, canCancel, canPayNow } from "../../../utils/bookingActionsUtils";
import { formatCurrencyDisplay } from "../../../utils/subscriptionUtils";
import { initiateBookingPayment } from "../../../utils/paymentFlowHelper";
import billingService from "../../../services/billingService";
import HBookingModal from "../../../hcomponents/HBookingModal";
import ConfirmationModal from "../host-calendar/ConfirmationModal";
import * as localService from "../../../services/localService";
import useBookingWebSocket from "../../../hooks/useBookingWebSocket";
import logger from "../../../utils/logger";


const MyBookings = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [viewMode, setViewMode] = useState("grid");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [_loading, setLoading] = useState(true);
  const [_error, setError] = useState(null);

  // Check subscription status for booking endpoint selection
  const { hasActiveSubscription } = useSubscription();
  const { handlePayNow } = useBookingFlow();

  // Modal states
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // WebSocket for real-time booking updates
  useBookingWebSocket(update => {
    setBookings(prev =>
      prev.map(b => (String(b.id) === String(update.bookingId) ? { ...b, status: update.newStatus } : b))
    );
  });
  const [currentBooking, setCurrentBooking] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [bookingForConfirmation, setBookingForConfirmation] = useState(null);
  const [payingBookingId, setPayingBookingId] = useState(null);
  const availableTimeSlotsForModal = [];
  const [selectedDateForBooking, setSelectedDateForBooking] = useState(new Date());
  const [selectedWorkspaceForBooking, setSelectedWorkspaceForBooking] = useState(null);
  const [bookingError, setBookingError] = useState(false);
  const [existingBookings, setExistingBookings] = useState([]);
  const [availabilityInstanceIds, setAvailabilityInstanceIds] = useState([]);

  // Define tabs for My Bookings
  const bookingTabs = [
    {
      label: "Upcoming",
      value: "upcoming",
    },
    {
      label: "Past",
      value: "past",
    },
    {
      label: "Canceled",
      value: "canceled",
    },
  ];

  // Fetch bookings and spaces from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const currentUser = localService.getUser();
        const userId = currentUser?.id;

        if (!userId) {
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }
        // Fetch bookings
        const timezone = getCurrentTimezoneOffset();
        const bookingsResponse = await spaceManagementService.getUserBookings(userId, {}, timezone);
        let fetchedBookings = [];
        if (bookingsResponse && bookingsResponse.status === 200 && bookingsResponse.data.content) {
          fetchedBookings = bookingsResponse.data.content.map(apiBooking => {
            const startTime = new Date(apiBooking.startTime);
            const endTime = new Date(apiBooking.endTime);
            const spaceInstance = apiBooking.spaceInstance || {};
            const locationObj = spaceInstance.location || {};
            const building = locationObj.buildingAddress || {};
            const bookedBy = apiBooking.bookedBy || {};
            const hostOrg = apiBooking.hostOrganization || {};

            // Collect all images from booking media and building
            const bookingMediaImages = apiBooking?.media?.images || [];
            const buildingImages = spaceInstance?.location?.buildingAddress?.images || [];
            const allSpaceImages = [...bookingMediaImages, ...buildingImages];
            const spaceImages = allSpaceImages
              .filter(img => {
                if (!img) return false;
                if (typeof img === "string") return img.trim() !== "";
                if (typeof img === "object" && img !== null) return img.resourceUrl || img.url || img.src;
                return false;
              })
              .map(img => {
                if (typeof img === "string") return img.trim();
                return (img.resourceUrl || img.url || img.src || "").trim();
              })
              .filter(img => img !== "");

            const guestName = bookedBy.firstName
              ? `${bookedBy.firstName} ${bookedBy.lastName || ""}`.trim()
              : bookedBy.username || "Guest";

            return {
              id: apiBooking.id,
              name: guestName,
              title: spaceInstance.title || `Booking #${apiBooking.id}`,
              location: building.streetAddress
                ? `${building.streetAddress}, ${building.city || ""}, ${building.region || ""}, ${building.country || ""}`
                : locationObj.name || "",
              type: spaceInstance.type || "",
              status: apiBooking.status,
              date: startTime.toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              }),
              time: `${startTime.toLocaleTimeString("en-US", { timeZone: getUserTimezone(), hour: "2-digit", minute: "2-digit" })} - ${endTime.toLocaleTimeString("en-US", { timeZone: getUserTimezone(), hour: "2-digit", minute: "2-digit" })}`,
              host:
                apiBooking.hostOrgName ||
                hostOrg?.name ||
                spaceInstance?.location?.buildingAddress?.organization?.name ||
                "",
              username: bookedBy.username || "",
              spaceImages,
              statusColor:
                apiBooking.status === "CHECKED_IN"
                  ? "bg-blue-100 text-blue-800"
                  : apiBooking.status === "PENDING"
                    ? "bg-yellow-100 text-yellow-800"
                    : apiBooking.status === "PENDING_PAYMENT"
                      ? "bg-amber-100 text-amber-800"
                      : apiBooking.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800",
              bookingDate: startTime,
              startTime,
              endTime,
              email: bookedBy.email || "",
              avatar:
                bookedBy.imageUrl ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  bookedBy.firstName || bookedBy.username || "Guest"
                )}&background=random`,
              floorLevel: locationObj.floorLevel,
              digitalAddress: building.digitalAddress,
              notes: apiBooking.notes,
              createdAt: new Date(apiBooking.createdAt),
              spaceId: apiBooking.spaceId, // Add spaceId for modal compatibility
              availabilitySlots: spaceInstance.availabilitySlots || [], // Extract availability slots from API
            };
          });
        }
        setBookings(fetchedBookings);
        // Fetch all published spaces (not just user's spaces, since customer books spaces from hosts)
        const spacesResponse = await spaceManagementService.searchSpaces({
          status: "PUBLISHED",
          pageNumber: 0,
          pageSize: 100,
        });
        let fetchedSpaces = [];
        if (spacesResponse && spacesResponse.status === 200 && spacesResponse.data.content) {
          fetchedSpaces = spacesResponse.data.content.map(space => {
            // Get the first instance for location and availability info
            const firstInstance = space.instances?.[0];
            const location = firstInstance?.location;
            const building = location?.buildingAddress;

            // Get availability slots - use space-level (superset) since we book spaces, not instances
            const availabilitySlots =
              space.availableSlots?.length > 0 ? space.availableSlots : firstInstance?.availabilitySlots || [];

            logger.log(`Space ${space.id} availability:`, {
              spaceSlots: space.availableSlots?.length,
              instanceSlots: firstInstance?.availabilitySlots?.length,
              using: availabilitySlots.length > 0 ? `space-level (${availabilitySlots.length} slots)` : "no slots",
            });

            return {
              id: space.id,
              name:
                firstInstance?.title ||
                getEventName(space, firstInstance) ||
                space.name ||
                `Space ${space.id}`,
              image:
                space.media?.images?.[0] ||
                space.images?.[0] ||
                building?.images?.[0] ||
                "/assets/img/Web/Spacia/imgplaceholder.png",
              location: building?.streetAddress
                ? `${building.streetAddress}, ${building.city || ""}`
                : location?.name || "Location not specified",
              capacity: space.capacity || 1,
              minimumDuration: space.minimumDuration || 1,
              maximumDuration: space.maximumDuration || 24,
              availableSlots: availabilitySlots,
              availabilitySlots: availabilitySlots,
              instances: space.instances || [],
              ratePrice: space.ratePrice || 0,
              currency: space.currency || "KES",
              duration: space.duration || "HOURLY",
            };
          });
        }
        setSpaces(fetchedSpaces);
        setLoading(false);
      } catch (err) {
        setError("Failed to load bookings or spaces");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Custom tab change handler (no routing, just state)
  const handleTabChange = value => {
    setActiveTab(value);
  };

  // Fetch existing bookings for a space using the availability endpoint
  const fetchExistingBookings = async spaceId => {
    logger.log("🟢 [AVAILABILITY] fetchExistingBookings called with spaceId:", spaceId);

    if (!spaceId) {
      setExistingBookings([]);
      setAvailabilityInstanceIds([]);
      return;
    }

    try {
      logger.log("🟢 [AVAILABILITY] Calling getBookingAvailability API...");
      const availabilityResponse = await spaceManagementService.getBookingAvailability({
        spaceId: spaceId,
      });

      logger.log("🟢 [AVAILABILITY] API Response:", availabilityResponse);

      if (availabilityResponse.status === 200 && availabilityResponse.data) {
        const { bookedSlots, instanceIds } = availabilityResponse.data;

        logger.log("🟢 [AVAILABILITY] Booked slots:", bookedSlots?.length || 0);
        logger.log("🟢 [AVAILABILITY] Instance IDs:", instanceIds?.length || 0);

        // Transform bookedSlots to match existingBookings format
        const bookings = (bookedSlots || []).map(slot => ({
          spaceInstanceId: slot.spaceInstanceId,
          startTime: slot.startTime,
          endTime: slot.endTime,
          status: slot.status,
        }));
        setExistingBookings(bookings);

        // Store instance IDs from availability response
        setAvailabilityInstanceIds(instanceIds || []);
      } else {
        logger.log("⚠️ [AVAILABILITY] No data in response");
        setExistingBookings([]);
        setAvailabilityInstanceIds([]);
      }
    } catch (error) {
      logger.error("❌ [AVAILABILITY] Error fetching booking availability:", error);
      setExistingBookings([]);
      setAvailabilityInstanceIds([]);
    }
  };

  // Open create booking modal
  const handleOpenCreateBookingModal = () => {
    history.push("/host/book-space");
  };

  // Create a booking from the modal
  const handleCreateBookingFromModal = async bookingData => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      let userId = null;
      if (currentUser) {
        try {
          const parsedUser = JSON.parse(currentUser);
          userId = parsedUser.id || parsedUser.userId;
        } catch (e) {
          logger.warn("Failed to parse user data:", e);
        }
      }
      if (!userId) {
        toastUtils.error("Authentication required", {
          description: "Please log in to create a booking.",
        });
        return;
      }

      if (!bookingData.selectedTimeSlot) {
        toastUtils.error("Please select a time slot");
        return;
      }

      // Find the selected workspace
      const workspace = spaces.find(w => w.id.toString() === bookingData.workspaceId.toString());
      if (!workspace) {
        toastUtils.error("Selected workspace not found");
        return;
      }

      const payload = {
        userId,
        workspaceId: workspace.id,
        startTime: convertToUTC(bookingData.selectedTimeSlot.startTime),
        endTime: convertToUTC(bookingData.selectedTimeSlot.endTime),
        timezone: getCurrentTimezoneOffset(),
        notes: bookingData.description || "Created from My Bookings page",
        emails: bookingData.emails || [],
      };

      logger.log("Creating booking with payload:", payload);

      // Use appropriate endpoint based on subscription status
      const response =
        hasActiveSubscription && !bookingError
          ? await spaceManagementService.createBooking(payload)
          : await spaceManagementService.createBookingPendingPayment(payload);

      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create booking");
        setShowBookingModal(false);
        setBookingError(false);

        // Check if this is a play service with an OFFLINE ticket URL
        if (workspace.tickets && workspace.tickets.length > 0) {
          const firstTicket = workspace.tickets[0];
          if (firstTicket.ticketType !== "DEFAULT" && firstTicket.url) {
            let finalUrl = firstTicket.url;
            if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
              finalUrl = "https://" + finalUrl;
            }
            logger.log("Redirecting to ticket URL:", finalUrl);
            toastUtils.success("Booking successful! Redirecting to tickets...", {
              description: "You will be redirected to complete your ticket purchase.",
            });
            setTimeout(() => {
              window.open(finalUrl, "_blank");
            }, 1500);
          }
        }

        // Refresh bookings
        await refreshBookings();
      } else {
        showApiMessage(response, "create booking");
        setBookingError(true);
      }
    } catch (error) {
      logger.error("Error creating booking:", error);
      showApiMessage(error, "create booking");
      setBookingError(true);
    }
  };

  // Create booking with pending payment
  const handleCreateBookingPendingPayment = async bookingData => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      let userId = null;
      if (currentUser) {
        try {
          const parsedUser = JSON.parse(currentUser);
          userId = parsedUser.id || parsedUser.userId;
        } catch (e) {
          logger.warn("Failed to parse user data:", e);
        }
      }
      if (!userId) {
        toastUtils.error("Authentication required", {
          description: "Please log in to create a booking.",
        });
        return;
      }

      if (!bookingData.selectedTimeSlot) {
        toastUtils.error("Please select a time slot");
        return;
      }

      // Find the selected workspace
      const workspace = spaces.find(w => w.id.toString() === bookingData.workspaceId.toString());
      if (!workspace) {
        toastUtils.error("Selected workspace not found");
        return;
      }

      const payload = {
        userId,
        workspaceId: workspace.id,
        startTime: convertToUTC(bookingData.selectedTimeSlot.startTime),
        endTime: convertToUTC(bookingData.selectedTimeSlot.endTime),
        timezone: getCurrentTimezoneOffset(),
        notes: bookingData.description || "Created from My Bookings page",
        emails: bookingData.emails || [],
      };

      logger.log("Creating booking with pending payment:", payload);

      // Use useBookingFlow hook for consistent payment flow
      const result = await handlePayNow(payload, {
        space: workspace,
        onError: error => {
          showApiMessage(error, "create booking");
          setBookingError(true);
        },
      });

      if (result?.success) {
        if (result?.isPlayService && result?.ticketUrl) {
          toastUtils.success("Booking created! Redirecting to tickets...", {
            description: "You will be redirected to complete your ticket purchase.",
          });
          setShowBookingModal(false);
          setTimeout(() => {
            window.open(result.ticketUrl, "_blank");
          }, 1500);
        } else {
          setShowBookingModal(false);
          if (result?.paymentResult?.success) {
            toastUtils.success("Payment initiated", {
              description: "Complete payment in the new tab to confirm your booking.",
            });
          }
        }

        setBookingError(false);
        await refreshBookings();
      }
    } catch (error) {
      logger.error("Error creating booking with pending payment:", error);
      showApiMessage(error, "create booking");
    }
  };

  const handleModifyBooking = async bookingId => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toastUtils.error("Booking not found");
      return;
    }

    // Validate that the booking can be modified
    if (!canModify(booking)) {
      const status = booking?.status?.toUpperCase();
      let message = "This booking cannot be modified.";

      if (status === "CHECKED_IN") {
        message = "Cannot edit booking after check-in. Please check out the guest first.";
      } else if (["CANCELLED", "CANCELED"].includes(status)) {
        message = "Cannot edit a cancelled booking.";
      } else if (status === "COMPLETED") {
        message = "Cannot edit a completed booking.";
      } else if (status === "NO_SHOW") {
        message = "Cannot edit a no-show booking.";
      } else if (status === "PENDING_PAYMENT") {
        message = "Cannot edit booking while payment is pending. Please complete or cancel the payment first.";
      }

      toastUtils.error(message);
      return;
    }

    logger.log("🔵 [CUSTOMER_BOOKING] Opening modify modal for booking:", booking);
    logger.log("🔵 [CUSTOMER_BOOKING] Booking spaceId:", booking.spaceId, "Type:", typeof booking.spaceId);
    logger.log(
      "🔵 [CUSTOMER_BOOKING] Available spaces:",
      spaces.map(s => ({ id: s.id, type: typeof s.id, name: s.name }))
    );

    // Fetch full booking details to get complete space data
    let spaceData = null;
    try {
      const bookingRes = await spaceManagementService.getBookingById(booking.id);
      if (bookingRes?.status === 200 && bookingRes?.data) {
        spaceData = bookingRes.data.space || bookingRes.data.spaceInstance || null;
      }
    } catch (err) {
      logger.error("🔴 [CUSTOMER_BOOKING] Failed to fetch booking details:", err);
    }

    // Get workspace — prefer the spaces list, enrich with API data
    let workspace = spaces.find(s => s.id === booking.spaceId || s.id.toString() === booking.spaceId.toString());

    if (workspace && spaceData) {
      // Merge API data into the workspace to fill missing fields (availableSlots, totalInstances, etc.)
      workspace = { ...workspace, ...spaceData, id: workspace.id };
    } else if (spaceData) {
      // Build workspace from API data
      workspace = { ...spaceData, id: spaceData.id || booking.spaceId };
    }

    if (!workspace) {
      toastUtils.error("Workspace not found");
      return;
    }

    logger.log("🔵 [CUSTOMER_BOOKING] Found workspace:", workspace);

    // Fetch availability data for this workspace (used by BookingTypeSelector for conflict coloring)
    logger.log("🔵 [CUSTOMER_BOOKING] Fetching availability for workspace:", workspace.id);
    await fetchExistingBookings(workspace.id);
    logger.log("🔵 [CUSTOMER_BOOKING] Availability fetched. Existing bookings:", existingBookings.length);

    // Use the booking's date for the modal
    const bookingDate = new Date(booking.startTime);
    setSelectedDateForBooking(bookingDate);
    setSelectedWorkspaceForBooking(workspace);

    // Prepare booking data for edit mode
    const bookingForEdit = {
      id: booking.id,
      startTime: booking.startTime,
      endTime: booking.endTime,
      notes: booking.notes || "",
      emails: booking.email ? [booking.email] : [],
      spaceId: booking.spaceId,
    };

    setCurrentBooking(bookingForEdit);
    setIsEditMode(true);
    setShowBookingModal(true);
  };

  const handleUpdateBookingFromModal = async (bookingId, updatePayload) => {
    try {
      const timezone = getCurrentTimezoneOffset();
      const response = await spaceManagementService.updateBooking(bookingId, updatePayload, timezone);
      if (response.status === 200) {
        showApiMessage(response, "update booking");
        setShowBookingModal(false);
        setCurrentBooking(null);
        setIsEditMode(false);

        // Refresh bookings
        await refreshBookings();
      } else {
        showApiMessage(response, "update booking");
      }
    } catch (error) {
      showApiMessage(error, "update booking");
    }
  };

  const refreshBookings = async () => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      let userId = null;
      if (currentUser) {
        try {
          const parsedUser = JSON.parse(currentUser);
          userId = parsedUser.id || parsedUser.userId;
        } catch (e) {
          logger.warn("Failed to parse user data:", e);
        }
      }

      if (!userId) return;

      const timezone = getCurrentTimezoneOffset();
      const bookingsResponse = await spaceManagementService.getUserBookings(userId, {}, timezone);
      if (bookingsResponse && bookingsResponse.status === 200 && bookingsResponse.data.content) {
        const transformedBookings = bookingsResponse.data.content.map(apiBooking => {
          const startTime = new Date(apiBooking.startTime);
          const endTime = new Date(apiBooking.endTime);
          const spaceInstance = apiBooking.spaceInstance || {};
          const locationObj = spaceInstance.location || {};
          const building = locationObj.buildingAddress || {};
          const bookedBy = apiBooking.bookedBy || {};
          const hostOrg = apiBooking.hostOrganization || {};

          // Collect all images from booking media and building
          const bookingMediaImages = apiBooking?.media?.images || [];
          const buildingImages = spaceInstance?.location?.buildingAddress?.images || [];
          const allSpaceImages = [...bookingMediaImages, ...buildingImages];
          const spaceImages = allSpaceImages
            .filter(img => {
              if (!img) return false;
              if (typeof img === "string") return img.trim() !== "";
              if (typeof img === "object" && img !== null) return img.resourceUrl || img.url || img.src;
              return false;
            })
            .map(img => {
              if (typeof img === "string") return img.trim();
              return (img.resourceUrl || img.url || img.src || "").trim();
            })
            .filter(img => img !== "");

          const guestName = bookedBy.firstName
            ? `${bookedBy.firstName} ${bookedBy.lastName || ""}`.trim()
            : bookedBy.username || "Guest";

          return {
            id: apiBooking.id,
            name: guestName,
            title: spaceInstance.title || `Booking #${apiBooking.id}`,
            location: building.streetAddress
              ? `${building.streetAddress}, ${building.city || ""}, ${building.region || ""}, ${building.country || ""}`
              : locationObj.name || "",
            floorLevel: locationObj.floorLevel,
            digitalAddress: building.digitalAddress,
            type: spaceInstance.type || "",
            status: apiBooking.status,
            date: startTime.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            time: `${startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${endTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
            host:
              apiBooking.hostOrgName ||
              hostOrg?.name ||
              spaceInstance?.location?.buildingAddress?.organization?.name ||
              "",
            username: bookedBy.username || "",
            notes: apiBooking.notes,
            createdAt: new Date(apiBooking.createdAt),
            spaceImages,
            statusColor:
              apiBooking.status === "CHECKED_IN"
                ? "bg-blue-100 text-blue-800"
                : apiBooking.status === "PENDING"
                  ? "bg-yellow-100 text-yellow-800"
                  : apiBooking.status === "PENDING_PAYMENT"
                    ? "bg-amber-100 text-amber-800"
                    : apiBooking.status === "COMPLETED"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800",
            bookingDate: startTime,
            startTime,
            endTime,
            email: bookedBy.email || "",
            avatar:
              bookedBy.imageUrl ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                bookedBy.firstName || bookedBy.username || "Guest"
              )}&background=random`,
            spaceId: apiBooking.spaceId,
            availabilitySlots: spaceInstance.availabilitySlots || [], // Extract availability slots from API
          };
        });
        setBookings(transformedBookings);
      }
    } catch (error) {
      logger.error("Failed to refresh bookings:", error);
    }
  };

  const handleCancelBooking = bookingId => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toastUtils.error("Booking not found");
      return;
    }

    setBookingForConfirmation(booking);
    setConfirmationAction("cancel");
    setShowConfirmationModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!bookingForConfirmation) return;

    try {
      const response = await spaceManagementService.cancelBooking(bookingForConfirmation.id);
      if (response.status === 200) {
        showApiMessage(response, "cancel booking");
        setShowConfirmationModal(false);
        setBookingForConfirmation(null);
        setConfirmationAction(null);

        // Refresh bookings
        await refreshBookings();
      } else {
        showApiMessage(response, "cancel booking");
      }
    } catch (error) {
      showApiMessage(error, "cancel booking");
    }
  };

  const handleCheckInBooking = bookingId => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toastUtils.error("Booking not found");
      return;
    }

    setBookingForConfirmation(booking);
    setConfirmationAction("check-in");
    setShowConfirmationModal(true);
  };

  const handleCheckOutBooking = bookingId => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) {
      toastUtils.error("Booking not found");
      return;
    }

    setBookingForConfirmation(booking);
    setConfirmationAction("check-out");
    setShowConfirmationModal(true);
  };

  const handlePayForExistingBooking = async bookingId => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;

    setPayingBookingId(bookingId);

    try {
      const currentUser = localService.getUser();
      if (!currentUser?.organizationId) {
        showApiMessage({ response: { status: 400, data: { message: "Organization ID not found" } } }, "pay booking");
        return;
      }

      let invoiceResponse = await billingService.getInvoices({
        clientReference: booking.id.toString(),
        organizationId: currentUser.organizationId,
        productType: "DIRECT_BOOKING",
        limit: 1,
      });

      if (
        !invoiceResponse.data?.data ||
        !Array.isArray(invoiceResponse.data.data) ||
        invoiceResponse.data.data.length === 0
      ) {
        const orgInvoices = await billingService.getInvoices({
          organizationId: currentUser.organizationId,
          productType: "DIRECT_BOOKING",
          limit: 50,
        });

        const data = orgInvoices.data?.data || [];
        const matchingInvoice = data.find(
          inv =>
            inv.clientReference &&
            (inv.clientReference.startsWith(`BK-${booking.id}-`) || inv.clientReference.startsWith(`BK${booking.id}`))
        );

        if (matchingInvoice) {
          invoiceResponse = { data: { data: [matchingInvoice] } };
        }
      }

      if (
        !invoiceResponse.data?.data ||
        !Array.isArray(invoiceResponse.data.data) ||
        invoiceResponse.data.data.length === 0
      ) {
        const fullBookingRes = await spaceManagementService.getBookingById(booking.id);
        if (fullBookingRes?.status === 200 && fullBookingRes?.data) {
          const fullBooking = fullBookingRes.data;
          let spaceData;
          const spaceRes = await spaceManagementService.getSpaceById(fullBooking.spaceId);

          if (spaceRes?.status === 200 && spaceRes?.data) {
            spaceData = spaceRes.data;
          } else {
            spaceData = fullBooking.space || fullBooking.spaceInstance;
          }

          if (!spaceData) {
            throw new Error("Space details not found for this booking");
          }

          const result = await initiateBookingPayment(fullBooking, spaceData);
          if (result.success) {
            return;
          } else {
            throw new Error("Failed to initiate payment flow");
          }
        } else {
          throw new Error("Could not fetch full booking details for payment");
        }
      }

      const invoice = invoiceResponse.data.data[0];
      if (!invoice.referenceCode) {
        showApiMessage(
          { response: { status: 400, data: { message: "Invoice reference code not found" } } },
          "pay booking"
        );
        return;
      }

      const paymentResponse = await billingService.initiatePayment({
        reference: invoice.referenceCode,
      });

      if (paymentResponse.data?.data?.checkoutUrl) {
        const checkoutUrl = paymentResponse.data.data.checkoutUrl;
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = checkoutUrl;
        } else {
          window.location.href = checkoutUrl;
        }
      } else {
        showApiMessage({ message: "Payment initiated successfully" }, "pay booking");
        refreshBookings();
      }
    } catch (error) {
      showApiMessage(error, "pay booking");
    } finally {
      setPayingBookingId(null);
    }
  };

  const handleConfirmCheckIn = async () => {
    if (!bookingForConfirmation) return;

    try {
      const response = await spaceManagementService.checkInBooking(bookingForConfirmation.id);
      if (response.status === 200) {
        showApiMessage(response, "check in");
        setShowConfirmationModal(false);
        setBookingForConfirmation(null);
        setConfirmationAction(null);

        // Refresh bookings
        await refreshBookings();
      } else {
        showApiMessage(response, "check in");
      }
    } catch (error) {
      showApiMessage(error, "check in");
    }
  };

  const handleConfirmCheckOut = async () => {
    if (!bookingForConfirmation) return;

    try {
      const response = await spaceManagementService.checkOutBooking(bookingForConfirmation.id);
      if (response.status === 200) {
        showApiMessage(response, "check out");
        setShowConfirmationModal(false);
        setBookingForConfirmation(null);
        setConfirmationAction(null);

        // Refresh bookings
        await refreshBookings();
      } else {
        showApiMessage(response, "check out");
      }
    } catch (error) {
      showApiMessage(error, "check out");
    }
  };

  // Calendar helper functions
  const formatTimeRange = (startTime, endTime) => {
    const start = startTime
      .toLocaleTimeString("en-US", {
        timeZone: getUserTimezone(),
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    const end = endTime
      .toLocaleTimeString("en-US", {
        timeZone: getUserTimezone(),
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return `${start} - ${end}`;
  };

  // Calendar View Component
  const CalendarView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const today = new Date();

    const days = [];
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Previous month days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({
        day: daysInPrevMonth - firstDayOfMonth + i + 1,
        month: prevMonth,
        year: prevYear,
        isCurrentMonth: false,
        fullDate: new Date(prevYear, prevMonth, daysInPrevMonth - firstDayOfMonth + i + 1),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month: month,
        year,
        isCurrentMonth: true,
        fullDate: new Date(year, month, i),
      });
    }

    // Next month days
    const remainingSlots = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;

    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        day: i,
        month: nextMonth,
        year: nextYear,
        isCurrentMonth: false,
        fullDate: new Date(nextYear, nextMonth, i),
      });
    }

    const isToday = dayObj => {
      return dayObj.day === today.getDate() && dayObj.month === today.getMonth() && dayObj.year === today.getFullYear();
    };

    const getDayBookings = dayObj => {
      return bookings.filter(booking => {
        return (
          booking.bookingDate.getDate() === dayObj.day &&
          booking.bookingDate.getMonth() === dayObj.month &&
          booking.bookingDate.getFullYear() === dayObj.year
        );
      });
    };

    return (
      <div className="bg-white rounded-lg">
        {/* Calendar Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">
            {currentDate.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </h2>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentDate(new Date(year, month - 1))}
              className="p-2 rounded hover:bg-gray-100"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date(year, month + 1))}
              className="p-2 rounded hover:bg-gray-100"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {daysOfWeek.map((day, index) => (
            <div key={index} className="p-3 text-center border-r border-gray-200 last:border-r-0">
              <div className="text-sm font-medium text-gray-900">{day}</div>
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 auto-rows-fr">
          {days.map((dayObj, index) => {
            const dayBookings = getDayBookings(dayObj);
            const isCurrentDay = isToday(dayObj);

            return (
              <div
                key={index}
                className="flex relative flex-col p-2 border-r border-b border-gray-200 last:border-r-0 min-h-32"
              >
                {/* Day number */}
                <div className="flex justify-between items-start mb-2">
                  <div className={`text-sm font-medium ${!dayObj.isCurrentMonth ? "text-gray-300" : "text-gray-900"}`}>
                    {dayObj.day}
                  </div>
                  {/* Today indicator */}
                  {isCurrentDay && (
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                      }}
                    ></div>
                  )}
                </div>

                {/* Bookings */}
                <div className="flex-1 space-y-1">
                  {dayBookings.slice(0, 3).map(booking => (
                    <div
                      key={booking.id}
                      className="px-2 py-1 text-xs font-medium text-left text-white rounded cursor-pointer hover:opacity-90"
                      style={{
                        backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                      }}
                      title={`${booking.title} - ${formatTimeRange(booking.startTime, booking.endTime)}`}
                    >
                      <div className="truncate">{booking.title}</div>
                      <div className="text-xs opacity-75">{formatTimeRange(booking.startTime, booking.endTime)}</div>
                    </div>
                  ))}

                  {dayBookings.length > 3 && (
                    <div className="text-xs text-gray-500">+{dayBookings.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    const status = booking.status ? booking.status.toUpperCase() : "";
    if (activeTab === "upcoming") {
      return status === "PENDING" || status === "PENDING_PAYMENT" || status === "CHECKED_IN";
    }
    if (activeTab === "past") {
      return status === "COMPLETED" || status === "NO_SHOW";
    }
    if (activeTab === "canceled") {
      return status === "CANCELED" || status === "CANCELLED";
    }
    return true;
  });

  // Image Carousel Component for booking cards
  const ImageCarousel = ({ images, title }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [hasError, setHasError] = useState(false);
    const hasMultipleImages = images && images.length > 1;

    const goToNext = e => {
      e?.stopPropagation();
      setCurrentImageIndex(prev => (prev + 1) % images.length);
      setHasError(false);
    };

    const goToPrev = e => {
      e?.stopPropagation();
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
      setHasError(false);
    };

    if (!images || images.length === 0 || hasError) {
      return (
        <div className="relative w-full h-48 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 overflow-hidden flex items-center justify-center border-2 border-dashed border-blue-200">
          <div className="flex flex-col items-center gap-2">
            <Building2 className="w-12 h-12 text-blue-400" />
            <span className="text-xs text-blue-500 font-medium">No images available</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className="relative w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={images[currentImageIndex]}
          alt={`${title || "Space"} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-all duration-300"
          onError={() => setHasError(true)}
        />
        {hasMultipleImages && (
          <>
            <button
              onClick={goToPrev}
              className={`absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 backdrop-blur-sm ${
                isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNext}
              className={`absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 backdrop-blur-sm ${
                isHovered ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={e => {
                    e?.stopPropagation();
                    setCurrentImageIndex(index);
                    setHasError(false);
                  }}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? "w-8 h-1.5 bg-white shadow-md"
                      : "w-1.5 h-1.5 bg-white/60 hover:bg-white/80"
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          <HActionButton
            label="New Booking"
            icon={<Calendar className="w-4 h-4" />}
            variant="primary"
            size="medium"
            onClick={handleOpenCreateBookingModal}
          />
        }
      />

      <div className="overflow-auto flex-1 p-6 bg-gray-50">
        <div className="mx-auto max-w-7xl">
          {/* Tabs and View Toggle */}
          <div className="flex justify-between items-center mb-8">
            <HTabsNavigation
              tabs={bookingTabs}
              value={activeTab}
              onTabChange={handleTabChange}
              className="mx-0"
              showMobileDropdown={true}
            />
            <BookingViewToggle activeView={viewMode} onViewChange={setViewMode} />
          </div>

          {/* Conditional rendering based on view mode */}
          {viewMode === "calendar" ? (
            <CalendarView />
          ) : viewMode === "grid" ? (
            <>
              {/* Booking Cards Grid */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredBookings.map(booking => (
                  <Card key={booking.id} className="overflow-hidden bg-white shadow-sm">
                    <div className="relative">
                      <ImageCarousel images={booking.spaceImages} title={booking.title} />
                      <div className="flex absolute top-3 left-3 gap-2">
                        <Badge
                          variant="secondary"
                          className={`${
                            booking.type === "Meeting Room"
                              ? "bg-purple-100 text-purple-800"
                              : booking.type === "Desk"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {booking.type}
                        </Badge>
                        <Badge className={booking.statusColor}>{booking.status}</Badge>
                      </div>
                    </div>

                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{booking.title}</h3>
                          <p className="flex gap-1 items-center text-sm text-gray-600">
                            <MapPin className="w-3 h-3" />
                            {booking.location}
                          </p>
                        </div>

                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex gap-2 items-center">
                            <Calendar className="w-4 h-4" />
                            {booking.date}
                          </div>
                          <div className="flex gap-2 items-center">
                            <Clock className="w-4 h-4" />
                            {booking.time}
                          </div>

                          {booking.floorLevel !== null &&
                            booking.floorLevel !== undefined &&
                            booking.floorLevel !== "" && (
                              <div className="flex gap-2 items-center">
                                <MapPin className="w-4 h-4" />
                                Floor: {booking.floorLevel}
                              </div>
                            )}
                          {booking.digitalAddress && (
                            <div className="flex gap-2 items-center">
                              <MapPin className="w-4 h-4" />
                              Digital: {booking.digitalAddress}
                            </div>
                          )}
                          {booking.notes && (
                            <div className="flex gap-2 items-start">
                              <div className="w-4 h-4 mt-0.5 flex-shrink-0">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                              </div>
                              <div className="text-xs italic text-gray-500">"{booking.notes}"</div>
                            </div>
                          )}
                          <div className="flex gap-2 items-center text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            Booked:{" "}
                            {booking.createdAt.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>

                          {(() => {
                            const space = spaces.find(s => s.id === booking.spaceId);
                            if (space && space.ratePrice) {
                              return (
                                <div className="flex gap-2 items-center text-sm font-semibold text-gray-900">
                                  <CreditCard className="w-4 h-4" />
                                  {formatCurrencyDisplay(space.ratePrice, space.currency)}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>

                        <div className="flex gap-2 pt-2">
                          {canModify(booking) && (
                            <button
                              onClick={() => handleModifyBooking(booking.id)}
                              className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-sm font-medium text-black rounded-md border border-black"
                            >
                              <Edit className="w-3 h-3" />
                              Modify
                            </button>
                          )}
                          {canPayNow(booking) && (
                            <button
                              onClick={() => handlePayForExistingBooking(booking.id)}
                              className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                              Pay Now
                            </button>
                          )}
                          {canCheckIn(booking) && (
                            <button
                              onClick={() => handleCheckInBooking(booking.id)}
                              className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-sm font-medium text-green-600 rounded-md border border-green-600"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Check In
                            </button>
                          )}
                          {canCheckOut(booking) && (
                            <button
                              onClick={() => handleCheckOutBooking(booking.id)}
                              className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-sm font-medium text-blue-600 rounded-md border border-blue-600"
                            >
                              <XCircle className="w-3 h-3" />
                              Check Out
                            </button>
                          )}
                          {canCancel(booking) && (
                            <button
                              onClick={() => handleCancelBooking(booking.id)}
                              className="flex flex-1 gap-2 justify-center items-center px-3 py-2 text-sm font-medium text-red-600 rounded-md border border-red-600"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Empty State */}
              {filteredBookings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 mb-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">No bookings found</h3>
                  <p className="mb-6 text-gray-600">You don't have any bookings yet. Start by booking a workspace!</p>
                  <HActionButton label="Book a Space" variant="outline" onClick={handleOpenCreateBookingModal} />
                </div>
              )}
            </>
          ) : viewMode === "list" ? (
            filteredBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 mb-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">No bookings found</h3>
                <p className="mb-6 text-gray-600">You don't have any bookings yet. Start by booking a workspace!</p>
                <HActionButton label="Book a Space" variant="outline" onClick={handleOpenCreateBookingModal} />
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg shadow">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="font-semibold">Reservation ID</TableHead>
                      <TableHead className="font-semibold">Space Name</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Date & Time</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Price</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map(booking => (
                      <TableRow key={booking.id} className="hover:bg-muted/50">
                        <TableCell>
                          <span className="font-semibold text-primary">
                            BK-{booking.id.toString().padStart(6, "0")}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center overflow-hidden">
                              {booking.spaceImages?.[0] ? (
                                <img
                                  src={booking.spaceImages[0]}
                                  alt={booking.title}
                                  className="w-full h-full object-cover"
                                  onError={e => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Building2 className="w-6 h-6 text-blue-400" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium">{booking.title}</div>
                              <div className="text-xs text-muted-foreground">{booking.type}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[220px]">
                            <div className="text-sm flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {booking.location}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="text-sm font-medium flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {booking.date}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {booking.time}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={booking.statusColor}>{booking.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const space = spaces.find(s => s.id === booking.spaceId);
                            if (space && space.ratePrice) {
                              return (
                                <span className="font-medium text-sm">
                                  {formatCurrencyDisplay(space.ratePrice, space.currency)}
                                </span>
                              );
                            }
                            return <span className="text-sm text-muted-foreground">—</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {canModify(booking) && (
                              <button
                                onClick={() => handleModifyBooking(booking.id)}
                                className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-black rounded-md border border-black hover:bg-gray-50"
                              >
                                <Edit className="w-3 h-3" />
                                Edit
                              </button>
                            )}
                            {canPayNow(booking) && (
                              <button
                                onClick={() => handlePayForExistingBooking(booking.id)}
                                className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-green-600 rounded-md border border-green-600 hover:bg-green-50"
                              >
                                <CreditCard className="w-3 h-3" />
                                Pay
                              </button>
                            )}
                            {canCheckIn(booking) && (
                              <button
                                onClick={() => handleCheckInBooking(booking.id)}
                                className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-green-600 rounded-md border border-green-600 hover:bg-green-50"
                              >
                                <CheckCircle className="w-3 h-3" />
                                Check In
                              </button>
                            )}
                            {canCheckOut(booking) && (
                              <button
                                onClick={() => handleCheckOutBooking(booking.id)}
                                className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-blue-600 rounded-md border border-blue-600 hover:bg-blue-50"
                              >
                                <XCircle className="w-3 h-3" />
                                Check Out
                              </button>
                            )}
                            {canCancel(booking) && (
                              <button
                                onClick={() => handleCancelBooking(booking.id)}
                                className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-red-600 rounded-md border border-red-600 hover:bg-red-50"
                              >
                                <X className="w-3 h-3" />
                                Cancel
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : null}
        </div>
      </div>

      {/* Modals */}
      {showBookingModal && (
        <HBookingModal
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            setCurrentBooking(null);
            setIsEditMode(false);
            setSelectedDateForBooking(new Date());
            setSelectedWorkspaceForBooking(null);
            setExistingBookings([]);
            setAvailabilityInstanceIds([]);
          }}
          selectedDate={selectedDateForBooking}
          selectedWorkspace={selectedWorkspaceForBooking}
          workspaces={spaces}
          onCreateBooking={handleCreateBookingFromModal}
          onCreateBookingPendingPayment={handleCreateBookingPendingPayment}
          onUpdateBooking={handleUpdateBookingFromModal}
          availableTimeSlots={availableTimeSlotsForModal}
          timeFormat="12h"
          hasActiveSubscription={hasActiveSubscription}
          bookingError={bookingError}
          existingBooking={currentBooking}
          isEditMode={isEditMode}
          allowBookingForCustomer={false}
          existingBookings={existingBookings}
          availabilityInstanceIds={availabilityInstanceIds}
        />
      )}

      {showConfirmationModal && bookingForConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false);
            setBookingForConfirmation(null);
            setConfirmationAction(null);
          }}
          booking={bookingForConfirmation}
          action={confirmationAction}
          onConfirm={
            confirmationAction === "cancel"
              ? handleConfirmCancel
              : confirmationAction === "check-in"
                ? handleConfirmCheckIn
                : confirmationAction === "check-out"
                  ? handleConfirmCheckOut
                  : handleConfirmCancel
          }
        />
      )}
    </HPageContainer>
  );
};

export default MyBookings;
